import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

interface CountdownOptions {
    startDate: string;
    labels: {
        days: string;
        hours: string;
        minutes: string;
        seconds: string;
    };
}

export function useCountdown(options: CountdownOptions) {
    const eventStarted = ref(false);
    const timerData = ref([]);
    const timerId = ref<number | null>(null);

    const startTs = computed(() => {
        const normalizedStartDate = options.startDate.replace(' ', 'T'); // For old iOS Safari

        const utcOffset = new Date().getTimezoneOffset() / 60;
        const utcOffsetDate = new Date(normalizedStartDate);

        utcOffsetDate.setHours(
            utcOffsetDate.getHours() - utcOffset,
        );

        return utcOffsetDate.getTime();
    });

    function toDhmsFormat(ms: number) {
        const days = Math.floor(ms / (24 * 60 * 60 * 1000));
        const daysms = ms % (24 * 60 * 60 * 1000);

        const hours = Math.floor(daysms / (60 * 60 * 1000));
        const hoursms = ms % (60 * 60 * 1000);

        const minutes = Math.floor(hoursms / (60 * 1000));
        const minutesms = ms % (60 * 1000);

        const sec = Math.floor(minutesms / 1000);

        const pad = (v: number) => (v >= 10 ? v : `0${v}`);

        return [
            { value: pad(days), unit: options.labels.days, class: 'timer-value' },
            { separator: true, class: 'timer-separator' },

            { value: pad(hours), unit: options.labels.hours, class: 'timer-value' },
            { separator: true, class: 'timer-separator' },

            { value: pad(minutes), unit: options.labels.minutes, class: 'timer-value' },
            { separator: true, class: 'timer-separator' },

            { value: pad(sec), unit: options.labels.seconds, class: 'timer-value' },
        ];
    }

    function tick() {
        if (eventStarted.value) return;

        const msLeft = startTs.value - Date.now();

        timerData.value = toDhmsFormat(msLeft);

        if (msLeft > 0) {
            timerId.value = window.setTimeout(tick, 1000);
        } else {
            eventStarted.value = true;
        }
    }

    onMounted(() => {
        timerData.value = toDhmsFormat(
            startTs.value - Date.now(),
        );

        tick();
    });

    onBeforeUnmount(() => {
        if (timerId.value) clearTimeout(timerId.value);
    });

    return {
        timerData,
        eventStarted,
    };
}
